import { useQuery } from "@apollo/client";
import { DashboardMetricsQuery } from "@/gql/graphql";
import { DASHBOARD_METRICS_QUERY } from "@/graphql/appointments";
import { useSalonStore } from "@/lib/store/salon";
import { useDashboardMetricsSubscription } from "./use-dashboard-metrics-subscription";

export interface UseDashboardMetricsReturn {
  data: DashboardMetricsQuery["dashboardMetrics"] | null;
  loading: boolean;
  error: Error | null;
  isConnected: boolean;
}

/**
 * Hook to get dashboard metrics data with real-time updates
 * This is a simplified version of useTodaysOverview that just returns raw data
 */
export const useDashboardMetrics = (): UseDashboardMetricsReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);

  // Enable real-time subscription for dashboard metrics
  const { isConnected: subscriptionConnected } = useDashboardMetricsSubscription({
    salonId: currentSalon?.id || "",
    enabled: !!currentSalon?.id,
  });

  // Fetch dashboard metrics from backend
  const {
    data: dashboardData,
    loading,
    error,
  } = useQuery(DASHBOARD_METRICS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
    },
    skip: !currentSalon?.id,
    fetchPolicy: "cache-first", // Use cache-first since subscriptions keep data fresh
  });

  return {
    data: dashboardData?.dashboardMetrics || null,
    loading,
    error: (error ?? null) as Error | null,
    isConnected: subscriptionConnected,
  };
};
